import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Select, MenuItem, Checkbox, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CustomDatePicker from '../form-components/CustomDatePicker';
import { dropdownMenuProps, selectFieldSx } from '../../utils/dropdownStyles';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import { useToast } from '../../context/ToastContext';
import { getUnassignedCharges, assignDepositId, uploadWireDocument } from '../../api/paymentApi';
import { formatAmount, formatDate } from '../../utils/currencyFormat';
import { validateWireDocumentFile, ACCEPTED_WIRE_DOCUMENT_ACCEPT } from '../../utils/wireDocumentValidation';

const PAGE_SIZE = 10;
// "Select all matching" fetches every row for the current filters in one call, the same
// convention TransactionTable.jsx's Excel export uses (MAX_EXPORT_ROWS) instead of paging
// through and accumulating client-side.
const MAX_SELECT_ALL_ROWS = 100000;

const STEP_TITLES = {
  1: 'Select Unassigned Transactions',
  2: 'Name the Deposit & Attach Proof',
  3: 'Review & Confirm',
};

const STEPS = [
  { n: 1, label: 'Select Transactions' },
  { n: 2, label: 'Name & Attach Proof' },
  { n: 3, label: 'Review & Confirm' },
];

const colHead = { color: '#64748b', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.07em', py: 1.75, bgcolor: '#f8fafc' };
const colCell = { fontSize: 12, color: '#1e293b', py: 1.5, borderBottom: '1px solid #f1f5f9' };

// Same badge configs/markup as TransactionTable.jsx (each file keeps its own small copy —
// see DepositDetail.jsx's TXN_TYPE_CONFIG/TxnTypeBadge for the existing precedent).
const GATEWAY_CONFIG = {
  Stripe: { bg: '#eef2ff', color: '#4f46e5' },
  PayPal: { bg: '#dbeafe', color: '#1d4ed8' },
};

const GatewayBadge = ({ gateway }) => {
  const cfg = GATEWAY_CONFIG[gateway] ?? { bg: '#f1f5f9', color: '#64748b' };
  return (
    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: cfg.bg, fontSize: 11, fontWeight: 700, color: cfg.color }}>
      {gateway}
    </Box>
  );
};

// Every row in this picker is, by definition, a captured charge with no Deposit ID yet —
// never Pending/Cancelled/Refund — so Transaction Type/Status are always these same two
// constant pills, not per-row data from the API.
const PurchaseTypeBadge = () => (
  <Box sx={{ display: 'inline-block', px: 1.25, py: 0.4, borderRadius: '8px', bgcolor: '#eff6ff', fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>
    <LocalText>Purchase</LocalText>
  </Box>
);

const SucceededStatusBadge = () => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: '#dcfce7', fontSize: 11, fontWeight: 700, color: '#15803d' }}>
    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#15803d', flexShrink: 0 }} />
    <LocalText>Succeeded</LocalText>
  </Box>
);

// "Add Deposit" — Finance-only, launched from the Deposits tab (never from Transaction
// History). Replaces the Deposits list view in place (same list <-> detail <-> add pattern
// DepositsTab.jsx already uses for drill-down), not a floating dialog. Step 1 picks unassigned
// charges from a full table styled like Transaction History, step 2 names the deposit and
// attaches its mandatory wire-transfer document, step 3 reviews and saves both in sequence
// (assignDepositId, then uploadWireDocument) via the existing space-scoped APIs.
const AssignDepositWizard = ({ onClose, onSaved }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);

  // Step 1 — unassigned charges picker
  const [charges, setCharges] = useState([]);
  const [loadingCharges, setLoadingCharges] = useState(false);
  const [chargesTotalCount, setChargesTotalCount] = useState(0);
  const [chargesPage, setChargesPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [selectedMap, setSelectedMap] = useState({}); // chargeId -> charge, survives pagination/filter changes
  const [selectingAll, setSelectingAll] = useState(false);

  // Step 2 — name + document
  const [depositId, setDepositId] = useState('');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef(null);

  // Save (step 3 confirm)
  const [saving, setSaving] = useState(false);
  const [assignSucceeded, setAssignSucceeded] = useState(false);
  // How many of the selected charges the backend actually assigned — can be fewer than
  // selected (already assigned elsewhere, cross-space, or now also: a different currency
  // than the deposit's, silently excluded server-side). Persisted in state, not re-derived
  // from chargeIds.length, so a retry-after-upload-failure still reports the real count.
  const [assignedCount, setAssignedCount] = useState(0);

  const depositIdPlaceholder = useLocalText('e.g. DEP-2026-08-04');
  const searchPlaceholder = useLocalText('Search by Intent ID, Customer Email or Name...');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const buildFilterParams = useCallback(() => ({
    search: search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    transactionType: transactionType || undefined,
  }), [search, dateFrom, dateTo, transactionType]);

  const fetchCharges = useCallback(() => {
    setLoadingCharges(true);
    getUnassignedCharges({ page: chargesPage, pageSize: PAGE_SIZE, ...buildFilterParams() })
      .then((data) => {
        setCharges(Array.isArray(data) ? data : data?.items ?? []);
        setChargesTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => { setCharges([]); setChargesTotalCount(0); })
      .finally(() => setLoadingCharges(false));
  }, [chargesPage, buildFilterParams]);

  useEffect(() => { if (step === 1) fetchCharges(); }, [step, fetchCharges]);
  useEffect(() => { setChargesPage(0); }, [search, dateFrom, dateTo, transactionType]);

  const chargesTotalPages = Math.max(1, Math.ceil(chargesTotalCount / PAGE_SIZE));
  const isDateRangeError = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  const hasActiveFilters = Boolean(searchInput || dateFrom || dateTo || transactionType);

  const resetFilters = () => {
    setSearchInput(''); setSearch('');
    setDateFrom(''); setDateTo(''); setTransactionType('');
  };

  const toggleCharge = (charge) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[charge.chargeId]) delete next[charge.chargeId];
      else next[charge.chargeId] = charge;
      return next;
    });
  };

  const allOnPageSelected = charges.length > 0 && charges.every((c) => !!selectedMap[c.chargeId]);
  const someOnPageSelected = charges.some((c) => !!selectedMap[c.chargeId]);

  const toggleSelectAllOnPage = () => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (allOnPageSelected) {
        charges.forEach((c) => { delete next[c.chargeId]; });
      } else {
        charges.forEach((c) => { next[c.chargeId] = c; });
      }
      return next;
    });
  };

  // Selects every charge matching the current filters, not just the visible page — a single
  // fetch with a large pageSize (same convention as TransactionTable.jsx's Excel export),
  // rather than paging through and accumulating.
  const selectAllMatching = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const data = await getUnassignedCharges({ page: 0, pageSize: MAX_SELECT_ALL_ROWS, ...buildFilterParams() });
      const allRows = Array.isArray(data) ? data : data?.items ?? [];
      setSelectedMap((prev) => {
        const next = { ...prev };
        allRows.forEach((c) => { next[c.chargeId] = c; });
        return next;
      });
    } catch {
      // error shown globally via axios interceptor → toast
    } finally {
      setSelectingAll(false);
    }
  };

  const clearSelection = () => setSelectedMap({});

  const selectedCharges = useMemo(() => Object.values(selectedMap), [selectedMap]);
  // netAmount is the settlement amount already converted into each charge's own transaction
  // currency — the only figure safe to sum/label with selectedCurrency below. It's null when
  // the backend couldn't confirm that conversion (see hasUnresolvedNetAmount); such charges are
  // blocked from proceeding past this step, so by the time this total is ever actually shown,
  // every selected charge is guaranteed to have a real netAmount — no merchantNetAmount fallback.
  const selectedTotal = useMemo(
    () => selectedCharges.reduce((sum, c) => sum + Number(c.netAmount ?? 0), 0),
    [selectedCharges],
  );
  const selectedCurrency = selectedCharges[0]?.currency;
  // A deposit represents one real bank deposit — definitionally one currency. The backend
  // (usp_Charge_AssignDepositId) now silently excludes any charge whose currency doesn't
  // match the deposit's, so flag a mixed selection here too rather than let the user find
  // out only after confirming, from a partial-success warning toast.
  const selectedCurrencies = useMemo(
    () => [...new Set(selectedCharges.map((c) => c.currency).filter(Boolean))],
    [selectedCharges],
  );
  const hasMixedCurrencies = selectedCurrencies.length > 1;
  // A charge's netAmount is null when its settlement currency differs from its own transaction
  // currency and the backend has no confirmed exchange rate to convert it back — there is no
  // currency-safe way to include it in this total. Block proceeding rather than silently
  // substituting its unconverted merchantNetAmount (that was the bug: a real EUR figure summed
  // and labeled as if it were USD).
  const hasUnresolvedNetAmount = selectedCharges.some((c) => c.netAmount == null);

  const handlePickFile = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    const err = validateWireDocumentFile(picked);
    if (err) { setFileError(err); setFile(null); return; }
    setFileError('');
    setFile(picked);
  };

  const canGoStep2 = selectedCharges.length > 0 && !hasMixedCurrencies && !hasUnresolvedNetAmount;
  const canGoStep3 = depositId.trim().length > 0 && !!file;

  const handleClose = () => { if (!saving) onClose(); };

  // Stepper only ever navigates backward — forward movement stays gated behind each step's
  // own "Next" validation (canGoStep2/canGoStep3). Once assignSucceeded, the deposit already
  // exists server-side, so re-visiting step 1/2 to change the charge selection or name would
  // be misleading — same rule the step 3 "Back" button already follows.
  const canGoToStep = (n) => n < step && !saving && !assignSucceeded;
  const goToStep = (n) => { if (canGoToStep(n)) setStep(n); };

  const handleConfirmSave = async () => {
    if (saving) return;
    setSaving(true);
    const chargeIds = selectedCharges.map((c) => c.chargeId);
    const trimmedDepositId = depositId.trim();
    try {
      let affected = assignedCount;
      if (!assignSucceeded) {
        const result = await assignDepositId(chargeIds, trimmedDepositId);
        affected = result?.affected ?? chargeIds.length;
        setAssignedCount(affected);
        setAssignSucceeded(true);
        onSaved?.();
      }
      await uploadWireDocument(trimmedDepositId, file);
      if (affected < chargeIds.length) {
        toast.warning(`Deposit ${trimmedDepositId} created with ${affected} of ${chargeIds.length} selected transactions — the rest didn't match (already assigned elsewhere, a different currency, or a different space) and are still unassigned.`);
      } else {
        toast.success(`Deposit ${trimmedDepositId} created with ${affected} transaction${affected !== 1 ? 's' : ''}.`);
      }
      onSaved?.();
      onClose();
    } catch {
      if (assignSucceeded) {
        toast.error('The deposit was created, but the document upload failed. Your file is still selected — retry the upload below.');
      }
      // else: the real reason is already shown globally via axios interceptor → toast
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.75, display: 'block' };
  const footerBtn = { px: 2, height: 36, border: '1px solid #dde6f0', borderRadius: '8px', bgcolor: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#64748b' };
  const primaryBtn = (disabled) => ({
    display: 'flex', alignItems: 'center', gap: 0.75, px: 2.25, height: 36, border: 'none', borderRadius: '8px',
    bgcolor: disabled ? '#94a3b8' : '#0a326d', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700, color: 'white',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── Breadcrumb ──────────────────────────────────── */}
      <Box onClick={handleClose} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, color: '#64748b', cursor: saving ? 'default' : 'pointer', '&:hover': saving ? {} : { textDecoration: 'underline' } }}>
        <ArrowBackIcon sx={{ fontSize: 14 }} />
        <Box component="span" fontWeight={700} sx={{ color: '#0a326d' }}><LocalText>Deposits</LocalText></Box>
        <Box component="span">/ <LocalText>Add Deposit</LocalText></Box>
      </Box>

      {/* ── Stepper ──────────────────────────────────────── */}
      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', p: 2, display: 'flex', alignItems: 'center' }}>
        {STEPS.map((s, i) => {
          const isCurrent = s.n === step;
          const isDone = s.n < step;
          const clickable = canGoToStep(s.n);
          return (
            <React.Fragment key={s.n}>
              <Box
                onClick={() => goToStep(s.n)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1, borderRadius: '10px',
                  bgcolor: isCurrent ? '#eff6ff' : 'transparent',
                  cursor: clickable ? 'pointer' : 'default', flexShrink: 0,
                  transition: 'background-color 0.15s ease',
                }}
              >
                <Box sx={{
                  width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 800, flexShrink: 0,
                  bgcolor: isDone || isCurrent ? '#0a326d' : '#eef2f6',
                  color: isDone || isCurrent ? 'white' : '#94a3b8',
                }}>
                  {isDone ? <CheckIcon sx={{ fontSize: 16 }} /> : s.n}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={10} fontWeight={700} sx={{ color: isCurrent ? '#0a326d' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1.4 }}>
                    <LocalText>Step</LocalText> {s.n}
                  </Typography>
                  <Typography fontSize={12.5} fontWeight={800} sx={{ color: isCurrent || isDone ? '#1e293b' : '#94a3b8', whiteSpace: 'nowrap' }}>
                    <LocalText>{s.label}</LocalText>
                  </Typography>
                </Box>
              </Box>
              {i < STEPS.length - 1 && (
                <Box sx={{ flex: 1, height: 0, borderTop: '2px dashed', borderColor: s.n < step ? '#0a326d' : '#e2e8f0', mx: 1 }} />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      <Box sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

        {/* ── Step 1: unassigned charges picker ───────────── */}
        {step === 1 && (
          <>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography fontSize={15} fontWeight={800} sx={{ color: '#1e293b' }}>
                      <LocalText>{STEP_TITLES[1]}</LocalText>
                    </Typography>
                    <Box sx={{ display: 'inline-block', px: 1.25, py: 0.3, borderRadius: '999px', bgcolor: '#fef3c7', fontSize: 10.5, fontWeight: 700, color: '#d97706' }}>
                      <LocalText>Action Required</LocalText>
                    </Box>
                  </Box>
                  <Typography fontSize={12} sx={{ color: '#94a3b8', mt: 0.5 }}>
                    <LocalText>Showing charges pending Deposit ID assignment. Select entries to bundle into a batch.</LocalText>
                  </Typography>
                </Box>

                {hasActiveFilters && (
                  <Box component="button" onClick={resetFilters} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ...footerBtn, height: 32, px: 1.5 }}>
                    <RefreshIcon sx={{ fontSize: 15 }} /> <LocalText>Reset Filters</LocalText>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mt: 2 }}>
                <TextField
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment> }}
                  sx={{ flex: '1 1 220px', maxWidth: 320, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, '& fieldset': { borderColor: '#dde6f0' }, '&:hover fieldset': { borderColor: '#5383b3' }, '&.Mui-focused fieldset': { borderColor: '#0a326d', borderWidth: 2 } } }}
                />
                <CustomDatePicker value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} placeholder="Start date" />
                <CustomDatePicker value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} placeholder="End date" isError={isDateRangeError} />
                <Select size="small" value={transactionType} onChange={(e) => setTransactionType(e.target.value)} displayEmpty sx={{ ...selectFieldSx, fontSize: 13, minWidth: 150 }} MenuProps={dropdownMenuProps}>
                  <MenuItem value=""><LocalText>All Payment Types</LocalText></MenuItem>
                  <MenuItem value="Purchase"><LocalText>Purchase</LocalText></MenuItem>
                  <MenuItem value="Refund"><LocalText>Refund</LocalText></MenuItem>
                </Select>
              </Box>
              {isDateRangeError && (
                <Typography fontSize={11} sx={{ color: '#ef4444', mt: 1 }}>
                  <LocalText>Start date must be before end date</LocalText>
                </Typography>
              )}
            </Box>

            <Box sx={{ px: 3, py: 1.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
              <Typography fontSize={12} sx={{ color: '#64748b' }}>
                <LocalText>Showing</LocalText> {charges.length} <LocalText>records</LocalText>
              </Typography>
              {chargesTotalCount > charges.length && (
                <>
                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}>·</Typography>
                  <Typography fontSize={12} sx={{ color: '#64748b' }}><LocalText>Select all on this page or</LocalText></Typography>
                  <Box component="button" onClick={selectAllMatching} disabled={selectingAll} sx={{ border: 'none', bgcolor: 'transparent', p: 0, fontSize: 12, fontWeight: 700, color: '#0a326d', cursor: selectingAll ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}>
                    {selectingAll ? <LocalText>Selecting…</LocalText> : <LocalText>Select all {chargesTotalCount} matching records</LocalText>}
                  </Box>
                </>
              )}
              {selectedCharges.length > 0 && (
                <>
                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}>·</Typography>
                  <Box component="button" onClick={clearSelection} sx={{ border: 'none', bgcolor: 'transparent', p: 0, fontSize: 12, fontWeight: 700, color: '#dc2626', cursor: 'pointer', textDecoration: 'underline' }}>
                    <LocalText>Clear selection</LocalText>
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 480 }}>
              {loadingCharges ? (
                <Box display="flex" justifyContent="center" p={6}>
                  <CircularProgress sx={{ color: '#0a326d' }} />
                </Box>
              ) : (
                <Table sx={{ minWidth: 1700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={colHead}>
                        <Checkbox size="small" checked={allOnPageSelected} indeterminate={someOnPageSelected && !allOnPageSelected} onChange={toggleSelectAllOnPage} disabled={charges.length === 0} />
                      </TableCell>
                      <TableCell sx={colHead}><LocalText>Payment Intent ID</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Transaction Type</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Status</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Customer Email / ID</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Customer Name</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Gateway</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Payment Method</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Date</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Currency</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Charged Amt</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Paid / Refund Amt</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Gateway Fee</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>FX Rate</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Contract %</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Net Amount</LocalText></TableCell>
                      <TableCell sx={{ ...colHead, textAlign: 'right' }}><LocalText>Settled Amount</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Deposit ID</LocalText></TableCell>
                      <TableCell sx={colHead}><LocalText>Refund Reason</LocalText></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {charges.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={19} sx={{ textAlign: 'center', py: 6, color: '#94a3b8', fontSize: 13 }}>
                          <LocalText>No unassigned transactions match these filters.</LocalText>
                        </TableCell>
                      </TableRow>
                    ) : (
                      charges.map((charge) => {
                        const checked = !!selectedMap[charge.chargeId];
                        return (
                          <TableRow
                            key={charge.chargeId}
                            onClick={() => toggleCharge(charge)}
                            sx={{ cursor: 'pointer', '&:last-child td': { border: 0 }, ...(checked ? { td: { bgcolor: '#eff6ff' } } : { '&:hover td': { bgcolor: '#fafbfc' } }) }}
                          >
                            <TableCell padding="checkbox" sx={colCell}>
                              <Checkbox size="small" checked={checked} onChange={() => toggleCharge(charge)} onClick={(e) => e.stopPropagation()} />
                            </TableCell>
                            <TableCell sx={colCell}>
                              <Typography fontSize={11} fontWeight={700} sx={{ color: '#1e293b', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                {charge.paymentIntentId}
                              </Typography>
                            </TableCell>

                            {/* Transaction Type / Status — every row here is, by definition, a
                                captured Purchase charge with no Deposit ID yet, never a
                                Pending/Cancelled/Refund row, so these are fixed pills. */}
                            <TableCell sx={colCell}><PurchaseTypeBadge /></TableCell>
                            <TableCell sx={colCell}><SucceededStatusBadge /></TableCell>

                            <TableCell sx={colCell}>
                              <Typography fontSize={12} sx={{ color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                {charge.customer ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}>
                              <Typography fontSize={12} sx={{ color: '#475569' }}>
                                {charge.customerName ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={colCell}>
                              {charge.gateway ? <GatewayBadge gateway={charge.gateway} /> : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>{charge.paymentMethod ?? '—'}</TableCell>
                            <TableCell sx={{ ...colCell, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(charge.chargeDate)}</TableCell>
                            <TableCell sx={{ ...colCell, color: '#64748b' }}>{charge.currency ?? '—'}</TableCell>

                            {/* Charged Amt */}
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Typography fontSize={13} sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                                {formatAmount(charge.chargedAmount, charge.currency)}
                              </Typography>
                            </TableCell>

                            {/* Paid / Refund Amt — always the charged amount itself here, never
                                a refund event (this picker only ever shows Purchase rows). */}
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Typography fontSize={13} fontWeight={700} sx={{ color: '#15803d', whiteSpace: 'nowrap' }}>
                                {formatAmount(charge.chargedAmount, charge.currency)}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {charge.gatewayFee != null ? formatAmount(charge.gatewayFee, charge.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {charge.exchangeRate != null ? Number(charge.exchangeRate).toFixed(4) : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {charge.contractPercentageApplied != null ? `${Number(charge.contractPercentageApplied).toFixed(2)}%` : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {charge.netAmount != null ? formatAmount(charge.netAmount, charge.currency) : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>
                            <TableCell sx={{ ...colCell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {charge.merchantNetAmount != null
                                ? formatAmount(charge.merchantNetAmount, charge.merchantNetCurrency ?? charge.currency)
                                : <span style={{ color: '#94a3b8' }}>—</span>}
                            </TableCell>

                            {/* Deposit ID — always empty: that's the definition of "unassigned". */}
                            <TableCell sx={{ ...colCell, whiteSpace: 'nowrap' }}><span style={{ color: '#94a3b8' }}>—</span></TableCell>
                            {/* Refund Reason — no refund events exist on a picker of raw charges. */}
                            <TableCell sx={{ ...colCell, color: '#64748b', maxWidth: 180 }}>—</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 1.5, borderTop: '1px solid #f1f5f9' }}>
              <Typography fontSize={11.5} sx={{ color: '#94a3b8' }}>
                <LocalText>Page</LocalText> {chargesPage + 1} <LocalText>of</LocalText> {chargesTotalPages}
              </Typography>
              <Box component="button" onClick={() => setChargesPage((p) => Math.max(0, p - 1))} disabled={chargesPage === 0}
                sx={{ px: 1.5, py: 0.5, border: '1px solid #dde6f0', borderRadius: '6px', bgcolor: 'white', fontSize: 11.5, color: chargesPage === 0 ? '#cbd5e1' : '#64748b', cursor: chargesPage === 0 ? 'not-allowed' : 'pointer' }}>
                <LocalText>Prev</LocalText>
              </Box>
              <Box component="button" onClick={() => setChargesPage((p) => Math.min(chargesTotalPages - 1, p + 1))} disabled={chargesPage >= chargesTotalPages - 1}
                sx={{ px: 1.5, py: 0.5, border: '1px solid #dde6f0', borderRadius: '6px', bgcolor: 'white', fontSize: 11.5, color: chargesPage >= chargesTotalPages - 1 ? '#cbd5e1' : '#64748b', cursor: chargesPage >= chargesTotalPages - 1 ? 'not-allowed' : 'pointer' }}>
                <LocalText>Next</LocalText>
              </Box>
            </Box>

            {selectedCharges.length > 0 && !hasMixedCurrencies && !hasUnresolvedNetAmount && (
              <Box sx={{ px: 3, py: 1.25, bgcolor: '#eff6ff', borderTop: '1px solid #dbeafe' }}>
                <Typography fontSize={12} fontWeight={700} sx={{ color: '#0a326d' }}>
                  {selectedCharges.length} <LocalText>selected</LocalText> · {formatAmount(selectedTotal, selectedCurrency)}
                </Typography>
              </Box>
            )}
            {hasMixedCurrencies && (
              <Box sx={{ px: 3, py: 1.25, bgcolor: '#fffbeb', borderTop: '1px solid #fde68a' }}>
                <Typography fontSize={12} fontWeight={700} sx={{ color: '#92400e' }}>
                  {selectedCharges.length} <LocalText>selected, mixing</LocalText> {selectedCurrencies.join(' + ')} —{' '}
                  <LocalText>a deposit can only include one currency. Deselect charges until only one currency remains.</LocalText>
                </Typography>
              </Box>
            )}
            {!hasMixedCurrencies && hasUnresolvedNetAmount && (
              <Box sx={{ px: 3, py: 1.25, bgcolor: '#fffbeb', borderTop: '1px solid #fde68a' }}>
                <Typography fontSize={12} fontWeight={700} sx={{ color: '#92400e' }}>
                  {selectedCharges.length} <LocalText>selected</LocalText> —{' '}
                  <LocalText>one or more charges have an unconfirmed settlement amount and can't be safely included in a deposit total. Deselect them to continue.</LocalText>
                </Typography>
              </Box>
            )}

            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Box component="button" onClick={handleClose} sx={footerBtn}><LocalText>Cancel</LocalText></Box>
              <Box component="button" onClick={() => canGoStep2 && setStep(2)} disabled={!canGoStep2} sx={primaryBtn(!canGoStep2)}>
                <LocalText>Next</LocalText>
              </Box>
            </Box>
          </>
        )}

        {/* ── Step 2: name + document ──────────────────────── */}
        {step === 2 && (
          <>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
              <Typography fontSize={15} fontWeight={800} sx={{ color: '#1e293b' }}>
                <LocalText>{STEP_TITLES[2]}</LocalText>
              </Typography>
            </Box>

            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ width: '100%', maxWidth: 420 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography component="label" sx={fieldLabel}><LocalText>Deposit ID</LocalText></Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={depositIdPlaceholder}
                    value={depositId}
                    onChange={(e) => setDepositId(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: 13, '& fieldset': { borderColor: '#dde6f0' }, '&:hover fieldset': { borderColor: '#5383b3' }, '&.Mui-focused fieldset': { borderColor: '#0a326d', borderWidth: 2 } } }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography component="label" sx={fieldLabel}><LocalText>Transfer Document</LocalText></Typography>
                  <input ref={fileInputRef} type="file" accept={ACCEPTED_WIRE_DOCUMENT_ACCEPT} style={{ display: 'none' }} onChange={handleFileChange} />
                  <Box
                    component="button"
                    type="button"
                    onClick={handlePickFile}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, width: '100%', border: `1px dashed ${file ? '#86efac' : '#94a3b8'}`, borderRadius: '8px', bgcolor: file ? '#f0fdf4' : '#f8fafc', color: file ? '#15803d' : '#0a326d', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', textAlign: 'left' }}
                  >
                    <UploadFileIcon sx={{ fontSize: 17, flexShrink: 0 }} />
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file ? file.name : <LocalText>Choose PDF, PNG, or JPEG (max 5 MB)</LocalText>}
                    </Box>
                  </Box>
                  {fileError && <Typography fontSize={12} sx={{ color: '#dc2626', mt: 1 }}>{fileError}</Typography>}
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Box component="button" onClick={() => goToStep(1)} sx={footerBtn}><LocalText>Back</LocalText></Box>
              <Box component="button" onClick={() => canGoStep3 && setStep(3)} disabled={!canGoStep3} sx={primaryBtn(!canGoStep3)}>
                <LocalText>Next</LocalText>
              </Box>
            </Box>
          </>
        )}

        {/* ── Step 3: review + confirm ─────────────────────── */}
        {step === 3 && (
          <>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f5f9' }}>
              <Typography fontSize={15} fontWeight={800} sx={{ color: '#1e293b' }}>
                <LocalText>{STEP_TITLES[3]}</LocalText>
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              {assignSucceeded && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', px: 2, py: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', mb: 2 }}>
                  <Typography fontSize={12} sx={{ color: '#92400e', lineHeight: 1.5 }}>
                    <LocalText>Deposit {depositId.trim()} was created successfully, but the document upload failed. Your file selection is still saved — click Retry Upload to try again.</LocalText>
                  </Typography>
                </Box>
              )}

              <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', p: 1.75, mb: 2, maxWidth: 420 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}><LocalText>Deposit ID</LocalText></Typography>
                  <Box sx={{ px: 1, py: 0.2, borderRadius: '6px', bgcolor: '#f1f5f9', fontSize: 11, fontWeight: 700, color: '#0a326d', fontFamily: 'monospace' }}>{depositId.trim()}</Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}><LocalText>Document</LocalText></Typography>
                  <Typography fontSize={12} fontWeight={600} sx={{ color: '#1e293b' }}>{file?.name ?? '—'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography fontSize={12} sx={{ color: '#94a3b8' }}><LocalText>Transactions included</LocalText></Typography>
                  <Typography fontSize={12} fontWeight={600} sx={{ color: '#1e293b' }}>{selectedCharges.length}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: '1px solid #e2e8f0' }}>
                  <Typography fontSize={13} fontWeight={800} sx={{ color: '#0a326d' }}><LocalText>Total Amount</LocalText></Typography>
                  <Typography fontSize={13} fontWeight={800} sx={{ color: '#0a326d' }}>{formatAmount(selectedTotal, selectedCurrency)}</Typography>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}><LocalText>Payment Intent ID</LocalText></TableCell>
                      <TableCell sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc' }}><LocalText>Customer</LocalText></TableCell>
                      <TableCell sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', bgcolor: '#f8fafc', textAlign: 'right' }}><LocalText>Net Amount</LocalText></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCharges.map((c) => (
                      <TableRow key={c.chargeId}>
                        <TableCell sx={{ fontSize: 11.5, color: '#64748b', fontFamily: 'monospace' }}>{c.paymentIntentId}</TableCell>
                        <TableCell sx={{ fontSize: 11.5, color: '#1e293b' }}>{c.customerName ?? c.customer ?? '—'}</TableCell>
                        {/* Net Amount, not Settled Amount — must match the Total Amount above,
                            which is a sum of Net Amounts (one currency); showing raw settlement
                            figures here (which can be in different currencies per charge) would
                            never visibly reconcile with that total. hasUnresolvedNetAmount blocks
                            reaching this step at all if any selected charge has no netAmount, so
                            no fallback/null-guard is needed here. */}
                        <TableCell sx={{ fontSize: 11.5, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>{formatAmount(c.netAmount, c.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {!assignSucceeded && (
                <Box component="button" onClick={() => goToStep(2)} disabled={saving} sx={footerBtn}><LocalText>Back</LocalText></Box>
              )}
              <Box component="button" onClick={handleConfirmSave} disabled={saving} sx={primaryBtn(saving)}>
                {saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <LocalText>{assignSucceeded ? 'Retry Upload' : 'Confirm & Save'}</LocalText>}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AssignDepositWizard;
